import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { ApiError } from '@/api/client';
import { fetchRfq, submitRfq, transitionRfq } from '@/api/rfqs';
import { PermissionGate } from '@/components/PermissionGate';
import { PriceAction } from '@/features/rfqs/PriceAction';
import { RFQ_STATUS_COLOUR } from '@/features/rfqs/statuses';
import type { RfqData, RfqStatus } from '@/types/generated';

/**
 * One request, on its own page.
 *
 * WHY THIS EXISTS AT ALL: the wizard has always navigated here on success and the route did not
 * exist, so finishing the wizard showed "this page does not exist" while the request was created
 * perfectly well — confusing enough that people submitted twice. The server side was already
 * finished: `fetchRfq`, `submitRfq` and `transitionRfq` were all written and none of them was
 * called from anywhere.
 *
 * TRANSITIONS COME FROM THE SERVER, NOT FROM A LOCAL MAP. `available_transitions` is computed from
 * the state machine and sent with the request precisely so a client cannot offer a move the API
 * would refuse — the alternative is a button that 422s, and a second copy of the machine to keep in
 * step. So the buttons here are a render of that array, and a status this page has never heard of
 * still produces a working button.
 *
 * TWO FIELDS ARE SHOWN WITH THEIR PROVENANCE, because that is why they are on the wire.
 * `chargeable_weight_basis` explains a chargeable weight three times the actual one — volume won —
 * and `distance_source` distinguishes a typed number from a routed one. Rendering either figure
 * bare would invite exactly the wrong conclusion.
 */

const TRANSITION_LABEL: Record<RfqStatus, string> = {
  draft: 'Return to draft',
  submitted: 'Submit',
  quoted: 'Mark quoted',
  won: 'Mark won',
  lost: 'Mark lost',
  expired: 'Mark expired',
  cancelled: 'Cancel request',
};

/** A loss with no reason is a data point nobody can learn from — the server insists too. */
const NEEDS_REASON: RfqStatus[] = ['lost', 'cancelled'];

export function RfqDetailPage() {
  const { id = '' } = useParams();

  const rfq = useQuery({
    queryKey: ['rfq', id],
    queryFn: () => fetchRfq(id),
    enabled: id !== '',
  });

  if (rfq.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (rfq.isError || !rfq.data) {
    return (
      <Alert severity="error" action={<Button component={RouterLink} to="/rfqs">All requests</Button>}>
        That request could not be loaded. It may have been deleted, or it may belong to another
        organisation.
      </Alert>
    );
  }

  return <Loaded rfq={rfq.data} />;
}

function Loaded({ rfq }: { rfq: RfqData }) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<RfqStatus | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['rfq', rfq.id] });
    void queryClient.invalidateQueries({ queryKey: ['rfqs'] });
  };

  const onError = (e: unknown) =>
    setError(e instanceof ApiError ? (e.detail ?? e.title) : 'That change could not be saved.');

  const submit = useMutation({
    mutationFn: () => submitRfq(rfq.id),
    onSuccess: invalidate,
    onError,
  });

  const move = useMutation({
    mutationFn: (to: RfqStatus) => transitionRfq(rfq.id, to, reason || undefined),
    onSuccess: () => {
      setPending(null);
      setReason('');
      invalidate();
    },
    onError,
  });

  // Submitting has its own endpoint because it has side effects a generic status change cannot
  // express, so it is separated from the rest of the machine here too.
  const transitions = rfq.available_transitions.filter((to) => to !== 'submitted');
  const canSubmit = rfq.available_transitions.includes('submitted');

  // SHOWN WHILE A DRAFT, DISABLED WHEN THE SERVER WOULD REFUSE. `available_transitions` is now narrowed by
  // `SubmitRfq`'s own guards, so its absence means the action would 422 — and `submit_blocked_reason` says
  // why. Disabled rather than hidden for the same reason as the quotation's Issue button: somebody looking
  // for how to submit a request needs to find the control and be told what is missing, not hunt for a
  // button that is not there.
  const showSubmit = canSubmit || rfq.status === 'draft';

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Button component={RouterLink} to="/rfqs" size="small">
          ← All requests
        </Button>
        <Typography variant="h5">{rfq.reference}</Typography>
        <Chip size="small" label={rfq.status} color={RFQ_STATUS_COLOUR[rfq.status]} />
        <Box sx={{ flexGrow: 1 }} />

        {showSubmit && (
          <PermissionGate permission="rfq:update">
            <Tooltip title={rfq.submit_blocked_reason ?? ''} placement="bottom">
              <span>
                <Button
                  variant="contained"
                  size="small"
                  disabled={submit.isPending || !canSubmit}
                  onClick={() => submit.mutate()}
                >
                  Submit
                </Button>
              </span>
            </Tooltip>
          </PermissionGate>
        )}

        <PriceAction rfq={rfq} size="medium" />

        {transitions.map((to) => (
          <PermissionGate key={to} permission="rfq:update">
            <Button size="small" onClick={() => setPending(to)}>
              {TRANSITION_LABEL[to]}
            </Button>
          </PermissionGate>
        ))}
      </Stack>

      {/* Stated on the page rather than only in a tooltip. This is a state, not an event, so it belongs
          here permanently rather than in a toast. */}
      {rfq.submit_blocked_reason !== null && rfq.status === 'draft' && (
        <Alert severity="warning">{rfq.submit_blocked_reason}</Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {rfq.has_lapsed && (
        <Alert severity="warning">
          The response deadline has passed. Pricing still works, but the customer may have moved on.
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            {rfq.customer_name}
          </Typography>
          <Divider sx={{ mb: 1.5 }} />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Facts
              rows={[
                ['Collects from', [rfq.origin_description, rfq.origin_city, rfq.origin_country]],
                [
                  'Delivers to',
                  [rfq.destination_description, rfq.destination_city, rfq.destination_country],
                ],
                ['Service', [rfq.service_category.replace(/_/g, ' ')]],
                ['Cargo', [rfq.cargo_type.replace(/_/g, ' ')]],
                ['Incoterm', [rfq.incoterm]],
              ]}
            />
            <Facts
              rows={[
                ['Gross weight', [`${rfq.weight_kg} kg`]],
                ['Volume', [rfq.volume_m3 === null ? null : `${rfq.volume_m3} m³`]],
                // The basis, not just the figure: a chargeable weight far above the actual one
                // looks like a bug until you can see that volume won.
                [
                  'Chargeable weight',
                  [
                    rfq.chargeable_weight_kg === null ? null : `${rfq.chargeable_weight_kg} kg`,
                    rfq.chargeable_weight_basis === 'not_applicable'
                      ? null
                      : `charged on ${rfq.chargeable_weight_basis} weight`,
                  ],
                ],
                ['Pieces', [rfq.piece_count === null ? null : String(rfq.piece_count)]],
                // A typed distance and a routed one are not equally trustworthy.
                [
                  'Distance',
                  [
                    rfq.distance_km === null ? null : `${rfq.distance_km} km`,
                    rfq.distance_source === null ? null : `(${rfq.distance_source.replace(/_/g, ' ')})`,
                  ],
                ],
              ]}
            />
            <Facts
              rows={[
                ['Currency', [rfq.currency]],
                ['Ready', [rfq.ready_date]],
                ['Delivery required', [rfq.required_delivery_date]],
                ['Respond by', [rfq.respond_by?.slice(0, 10) ?? null]],
                ['Handling', [flags(rfq) || null]],
              ]}
            />
          </Stack>

          {rfq.notes && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" display="block">
                Notes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {rfq.notes}
              </Typography>
            </>
          )}

          {rfq.lost_reason && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Lost: {rfq.lost_reason}
            </Alert>
          )}
        </CardContent>
      </Card>

      {rfq.line_items && rfq.line_items.length > 0 && (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Line</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell>Unit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rfq.line_items.map((line, index) => (
                <TableRow key={index}>
                  <TableCell>{line.description}</TableCell>
                  <TableCell align="right">{line.quantity}</TableCell>
                  <TableCell>{line.unit_of_measure.replace(/_/g, ' ')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={pending !== null} onClose={() => setPending(null)} fullWidth maxWidth="xs">
        <DialogTitle>{pending ? TRANSITION_LABEL[pending] : ''}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required={pending !== null && NEEDS_REASON.includes(pending)}
            helperText={
              pending !== null && NEEDS_REASON.includes(pending)
                ? 'Required. Without it this is a data point nobody can learn from.'
                : 'Optional.'
            }
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              move.isPending ||
              (pending !== null && NEEDS_REASON.includes(pending) && reason.trim() === '')
            }
            onClick={() => pending && move.mutate(pending)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/** Label/value pairs where a value is several parts, any of which may be absent. */
function Facts({ rows }: { rows: [string, (string | null | undefined)[]][] }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {rows.map(([label, parts]) => {
        const value = parts.filter(Boolean).join(' · ');

        return (
          <Box key={label} sx={{ py: 0.5 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {label}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {value === '' ? '—' : value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function flags(rfq: RfqData): string {
  return [
    rfq.temperature_controlled && 'temperature controlled',
    rfq.hazardous && `hazardous${rfq.un_number ? ` (${rfq.un_number})` : ''}`,
    rfq.oversized && 'oversized',
    rfq.high_value && 'high value',
  ]
    .filter(Boolean)
    .join(' · ');
}
